import { CartUpdatedEvent } from '@theme/events';

const SOURCE = 'cart-drawer-upsell';

/**
 * Verrou GLOBAL : le tiroir panier est reconstruit à chaque mise à jour, ce qui
 * crée un nouvel élément <cart-upsell-sync>. Un verrou porté par l'instance
 * laisserait deux éléments réconcilier en même temps et dupliquer les lignes.
 */
let syncing = false;

/**
 * IMPORTANT : Shopify régénère les clés de lignes à chaque modification du
 * panier, même pour les lignes qui ne sont pas touchées. Une clé lue avant une
 * requête n'est donc plus valable après. On relit toujours le panier juste
 * avant d'agir, et on retrouve une ligne par son identité (variante et
 * propriétés), jamais par une clé mémorisée.
 */

function sectionsToRender() {
  const sections = ['auto-gifts'];
  const drawer = document.querySelector('cart-drawer');
  if (drawer) sections.push(drawer.dataset.sectionId);
  return sections.join(',');
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  return { ok: response.ok, data };
}

async function getCart() {
  try {
    return await (await fetch('/cart.js')).json();
  } catch (error) {
    return null;
  }
}

function toastError(message) {
  document.dispatchEvent(
    new CustomEvent('toast:open', { detail: { type: 'error', message } }),
  );
}

function announce(sections, itemCount) {
  document.dispatchEvent(
    new CartUpdatedEvent({}, { itemCount, source: SOURCE, sections }),
  );
}

/** Propriétés d'une ligne, sans les valeurs vides. */
function cleanProps(props) {
  const out = {};
  Object.keys(props || {}).forEach((key) => {
    const value = props[key];
    if (value !== null && value !== undefined && value !== '') out[key] = String(value);
  });
  return out;
}

/** Deux jeux de propriétés sont-ils identiques, valeurs vides ignorées ? */
function sameProps(a, b) {
  const x = cleanProps(a);
  const y = cleanProps(b);
  const kx = Object.keys(x).sort();
  const ky = Object.keys(y).sort();
  if (kx.length !== ky.length) return false;
  return kx.every((key, i) => key === ky[i] && x[key] === y[key]);
}

/**
 * Redessine le tiroir d'après l'état RÉEL du panier, quel que soit le
 * résultat de l'opération : le client doit toujours voir ce qui s'est passé.
 */
async function refreshDrawer() {
  try {
    const [response, cart] = await Promise.all([
      fetch(window.location.pathname + '?sections=' + sectionsToRender()),
      getCart(),
    ]);
    if (!response.ok) return;
    const sections = await response.json();
    announce(sections, cart ? cart.item_count : undefined);
  } catch (error) {
    console.error('Error:', error);
  }
}

/**
 * Encart d'ajout de l'option dans le tiroir panier.
 * Marque la ligne de la montre choisie avec une propriété visible et ajoute
 * l'option.
 *
 * Une montre seule sur sa ligne est marquée EN PLACE, en une requête, sans
 * rien ajouter ni retirer. Une ligne de plusieurs exemplaires est réduite d'un
 * exemplaire, puis cet exemplaire est rajouté marqué avec son option. Dans les
 * deux cas, un échec est annulé : le client ne perd jamais une montre et ne
 * garde jamais une montre marquée sans option payée.
 */
class CartUpsellAddon extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('[data-ref="add-addon-button"]');
    this.select = this.querySelector('[data-ref="target-select"]');
    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  async handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.classList.contains('is-loading')) return;

    const target = this.select?.selectedOptions?.[0];
    if (!target) return;

    const renderedKey = target.value;
    const variantId = Number(target.dataset.variantId);
    const watchTitle = target.dataset.title || '';
    const propName = this.dataset.propName;
    const propValue = this.dataset.propValue;

    const optionProperties = {};
    optionProperties[this.dataset.refName] = watchTitle;

    this.classList.add('is-loading');

    try {
      const cart = await getCart();
      if (!cart) throw new Error('Panier illisible');

      // La clé affichée peut déjà être périmée : à défaut, on retrouve la
      // montre par sa variante, parmi les lignes non encore marquées.
      let line = cart.items.find((item) => item.key === renderedKey);
      if (!line) {
        line = cart.items.find(
          (item) => item.variant_id === variantId
            && !(item.properties && item.properties[propName] === propValue),
        );
      }
      if (!line) {
        toastError("Cette montre n'est plus dans le panier.");
        return;
      }

      const baseProps = cleanProps(line.properties);
      const markedProps = { ...baseProps };
      markedProps[propName] = propValue;

      if (line.quantity === 1) {
        const marked = await postJSON('/cart/change.js', {
          id: line.key,
          quantity: 1,
          properties: markedProps,
        });
        if (!marked.ok) {
          toastError(marked.data.description || marked.data.message);
          return;
        }

        const added = await postJSON('/cart/add.js', {
          items: [{ id: this.dataset.variantId, quantity: 1, properties: optionProperties }],
        });

        if (!added.ok) {
          // Annulation : on retire la mention posée juste avant.
          const fresh = await getCart();
          const markedLine = fresh && fresh.items.find(
            (item) => item.variant_id === variantId && sameProps(item.properties, markedProps),
          );
          if (markedLine) {
            await postJSON('/cart/change.js', {
              id: markedLine.key,
              quantity: markedLine.quantity,
              properties: baseProps,
            });
          }
          toastError(added.data.description || added.data.message);
        }
        return;
      }

      const reduced = await postJSON('/cart/change.js', {
        id: line.key,
        quantity: line.quantity - 1,
      });
      if (!reduced.ok) {
        toastError(reduced.data.description || reduced.data.message);
        return;
      }

      const added = await postJSON('/cart/add.js', {
        items: [
          { id: variantId, quantity: 1, properties: markedProps },
          { id: this.dataset.variantId, quantity: 1, properties: optionProperties },
        ],
      });

      if (!added.ok) {
        // Annulation : on remet l'exemplaire retiré, non marqué.
        await postJSON('/cart/add.js', {
          items: [{ id: variantId, quantity: 1, properties: baseProps }],
        });
        toastError(added.data.description || added.data.message);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      await refreshDrawer();
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-upsell-addon')) {
  customElements.define('cart-upsell-addon', CartUpsellAddon);
}

/**
 * Garde-fou : le nombre de montres portant la mention ne doit jamais dépasser
 * le nombre d'options payées. Si le client supprime ou réduit la ligne option,
 * la mention est retirée des montres concernées.
 *
 * Le retrait se fait en une seule requête change.js qui vide la propriété :
 * aucune ligne n'est supprimée puis recréée, donc aucun risque d'ajouter un
 * article en trop. L'opération ne retire jamais que des mentions et n'ajoute
 * jamais de ligne payante.
 */
class CartUpsellSync extends HTMLElement {
  constructor() {
    super();
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  connectedCallback() {
    document.addEventListener('cart:updated', this.handleCartUpdate);
    this.reconcile();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    if (event?.detail?.data?.source === SOURCE) return;
    this.reconcile();
  }

  #analyse(cart) {
    const propName = this.dataset.propName;
    const propValue = this.dataset.propValue;
    const addonProductId = Number(this.dataset.addonProductId);

    let optionUnits = 0;
    const flagged = [];

    for (const item of cart.items) {
      if (item.product_id === addonProductId) {
        optionUnits += item.quantity;
      } else if (item.properties && item.properties[propName] === propValue) {
        flagged.push(item);
      }
    }

    return { flagged, optionUnits };
  }

  async reconcile() {
    if (syncing) return;
    syncing = true;

    try {
      let lastSections = null;

      // On revérifie le panier après chaque opération plutôt que de boucler sur
      // une liste figée : l'état réel fait foi à chaque passe.
      for (let pass = 0; pass < 5; pass += 1) {
        const cart = await getCart();
        if (!cart) break;

        const { flagged, optionUnits } = this.#analyse(cart);
        const flaggedUnits = flagged.reduce((total, item) => total + item.quantity, 0);
        const excess = flaggedUnits - optionUnits;

        if (excess <= 0 || flagged.length === 0) break;

        const line = flagged[0];
        const take = Math.min(excess, line.quantity);

        if (take >= line.quantity) {
          // Toute la ligne perd sa mention : une seule requête, sans risque.
          const cleared = {};
          cleared[this.dataset.propName] = null;

          const res = await postJSON('/cart/change.js', {
            id: line.key,
            quantity: line.quantity,
            properties: cleared,
            sections: sectionsToRender(),
            sections_url: window.location.pathname,
          });

          if (!res.ok) break;
          lastSections = res.data.sections || lastSections;
        } else {
          // Retrait partiel : on scinde la ligne, en vérifiant que la réduction
          // a bien été prise en compte avant de réajouter les exemplaires.
          const reduced = await postJSON('/cart/change.js', {
            id: line.key,
            quantity: line.quantity - take,
          });

          if (!reduced.ok) break;

          // La clé a changé avec la réduction : on retrouve la ligne par son
          // identité. Comparer à l'ancienne clé faisait échouer ce contrôle à
          // tort, et les exemplaires retirés n'étaient jamais réajoutés.
          const remaining = (reduced.data.items || []).find(
            (item) => item.variant_id === line.variant_id && sameProps(item.properties, line.properties),
          );
          const remainingQuantity = remaining ? remaining.quantity : 0;
          if (remainingQuantity !== line.quantity - take) break;

          const unmarked = cleanProps(line.properties);
          delete unmarked[this.dataset.propName];

          const added = await postJSON('/cart/add.js', {
            id: line.variant_id,
            quantity: take,
            properties: unmarked,
            sections: sectionsToRender(),
            sections_url: window.location.pathname,
          });

          if (!added.ok) break;
          lastSections = added.data.sections || lastSections;
        }
      }

      if (lastSections) announce(lastSections, undefined);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      syncing = false;
    }
  }
}

if (!customElements.get('cart-upsell-sync')) {
  customElements.define('cart-upsell-sync', CartUpsellSync);
}